/**
 * @fileoverview Pure date-window and metric-delta math for period-over-period
 * comparisons on the product statistics page.
 * @module features/home/utils/metricDeltas
 */

import { differenceInCalendarDays, subDays } from 'date-fns'

export interface PreviousWindow {
  prevStart: Date
  prevEnd: Date
}

/**
 * Computes the equal-length window immediately preceding [startDate, endDate],
 * with no gap: prevEnd = startDate - 1 day, prevStart keeps the window length.
 */
export function computePreviousWindow(startDate: Date, endDate: Date): PreviousWindow {
  const prevEnd = subDays(startDate, 1)
  const windowDays = differenceInCalendarDays(endDate, startDate)
  return { prevStart: subDays(prevEnd, windowDays), prevEnd }
}

export interface MetricDelta {
  /** percent: value is a ratio (0.12 = +12%); absolute: value is in metric units */
  kind: 'percent' | 'absolute'
  value: number
  direction: 'up' | 'down'
  /** Whether the change is good news - drives green/red/muted rendering */
  sentiment: 'positive' | 'negative' | 'neutral'
}

export interface MetricDeltaOptions {
  /**
   * Render the delta without good/bad coloring. For metrics like ad spend,
   * where an increase is not inherently good or bad news.
   */
  neutralSentiment?: boolean
  /**
   * Fall back to an absolute delta when previous <= 0. Percent change across a
   * sign flip is meaningless (e.g. profit going -200 -> +500 is not "-350%").
   */
  absoluteWhenPrevNonPositive?: boolean
}

export function computeMetricDelta(
  current: number,
  previous: number | undefined,
  options: MetricDeltaOptions = {}
): MetricDelta | null {
  if (previous === undefined) return null

  if (options.absoluteWhenPrevNonPositive && previous <= 0) {
    const value = current - previous
    if (value === 0) return null
    return {
      kind: 'absolute',
      value,
      direction: value > 0 ? 'up' : 'down',
      sentiment: sentimentFor(value > 0, options.neutralSentiment),
    }
  }

  if (previous === 0) return null

  const value = (current - previous) / previous
  return {
    kind: 'percent',
    value,
    direction: value >= 0 ? 'up' : 'down',
    sentiment: sentimentFor(value >= 0, options.neutralSentiment),
  }
}

function sentimentFor(isIncrease: boolean, neutral?: boolean): 'positive' | 'negative' | 'neutral' {
  if (neutral) return 'neutral'
  return isIncrease ? 'positive' : 'negative'
}
